import { Button, Tooltip, ButtonMode, Icon, ICONS, Loader } from '@creditsights/styleguide-web';
import React, { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import { useForm, RegisterOptions } from 'react-hook-form';
import { useOutletContext } from 'react-router-dom';
import useFetchUserProfile from '../../../../hooks/profile/useFetchUserProfile';
import { WatchlistsOutletContext } from '../WatchlistsTab';
import { WATCHLIST_NAME_MAX_LENGTH } from './WatchlistName.logic';
import { WatchList } from '../../../../models/WatchList';
import { isEscapeKey } from '../../../../utils/inputEventUtils';

import styles from './WatchlistName.module.scss';
import { removeSubsequentSpaces } from '../../../../utils/inputFormatUtils';

const cx = classNames.bind(styles);

interface WatchListNameProps {
  watchlist: WatchList;
  watchlistsNames?: string[];
  isNewWatchlist?: boolean;
  onActiveChanged: (isActive: boolean) => void;
}

type WatchlistNameForm = {
  name: string;
};

const WatchlistName = ({
  watchlist,
  watchlistsNames,
  isNewWatchlist,
  onActiveChanged,
}: WatchListNameProps): React.ReactElement => {
  const { data: profile } = useFetchUserProfile();
  const [isSharedWatchlist, setSharedWatchlist] = useState(true);
  const { onNewWatchlistCreate, onWatchlistUpdate } = useOutletContext<WatchlistsOutletContext>();
  const [showInput, setShowInput] = useState(false);

  useEffect(() => {
    if (profile && watchlist) {
      setSharedWatchlist(watchlist.ownerId !== profile.userId);
    }
  }, [profile, watchlist]);

  const { register, handleSubmit, setFocus, formState, reset, watch, setValue } = useForm<WatchlistNameForm>({
    mode: 'onChange',
    defaultValues: {
      name: watchlist.name,
    },
  });

  const { isDirty, isValid, errors, isSubmitting } = formState;

  const onSubmit = ({ name }) => {
    if (!isDirty && !isNewWatchlist) {
      return setShowInput(false);
    }

    setShowInput(false);
    const trimmedName = name.trim();
    return isNewWatchlist
      ? onNewWatchlistCreate(trimmedName)
      : onWatchlistUpdate({
          id: watchlist.id,
          watchList: {
            name: trimmedName,
          },
        });
  };

  const handleChange = (e) => {
    setValue('name', removeSubsequentSpaces(e.target.value));
  };

  const handleBlur = () => {
    if (!isValid) {
      setShowInput(false);

      return reset({ name: watchlist.name });
    }

    if (!isDirty && !isNewWatchlist) {
      return setShowInput(false);
    }

    return handleSubmit(onSubmit)();
  };

  const handleKeyDown = (e) => {
    if (isEscapeKey(e)) {
      setShowInput(false);
      reset({ name: watchlist.name });
    }
  };

  useEffect(() => {
    onActiveChanged(showInput);
  }, [showInput]);

  const handleEditClick = () => {
    setShowInput(true);
  };

  const isUnique: RegisterOptions['validate'] = (name: string) => {
    const trimmedName = name.trim();
    return watchlistsNames?.includes(trimmedName) ? `${trimmedName} already exists` : true;
  };

  const hasValue: RegisterOptions['validate'] = (name: string) => {
    return !!name || 'The watchlist name cannot be empty';
  };

  useEffect(() => {
    setShowInput(false);
    reset({ name: watchlist.name });
  }, [watchlist.name]);

  useEffect(() => {
    if (isNewWatchlist) {
      handleEditClick();
    }
  }, [isNewWatchlist]);

  useEffect(() => {
    if (showInput) {
      setFocus('name');
    }
  }, [showInput]);

  return (
    <div className={cx('body')}>
      <div className={cx('heading')}>
        <h1 className={cx('title')}>{watch('name')}</h1>
        {!isSharedWatchlist && (
          <>
            {isSubmitting && (
              <div className={cx('loader-container')}>
                <Loader color="primary" text={null} />
              </div>
            )}
            {!isSubmitting && (
              <Tooltip
                content="Edit"
                trigger={
                  <Button
                    className={cx('edit-button')}
                    mode={ButtonMode.TERTIARY}
                    onClick={handleEditClick}
                    dataTestId="edit-watchlist-name"
                    ariaLabel="edit"
                    hasEqualSidesPadding
                  >
                    <Icon name={ICONS.Edit} />
                  </Button>
                }
              />
            )}
          </>
        )}
      </div>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <form
        onKeyDown={handleKeyDown}
        onSubmit={handleSubmit(onSubmit)}
        className={cx('form', { 'form--hidden': !showInput || isSubmitting })}
      >
        <div className={cx('field')}>
          <input
            autoComplete="off"
            id="name"
            data-testid="watchlist-name"
            className={cx('input', {
              'input--error': !isValid,
            })}
            type="text"
            disabled={!showInput}
            {...register('name', {
              onBlur: handleBlur,
              onChange: handleChange,
              validate: {
                isUnique,
                hasValue,
              },
              maxLength: {
                value: WATCHLIST_NAME_MAX_LENGTH,
                message: `The name can’t have more than ${WATCHLIST_NAME_MAX_LENGTH} characters`,
              },
            })}
          />
        </div>
        {!isValid && (
          <div className={cx('error')}>
            {Object.values(errors).map((e) => (
              <p className={cx('error-message')} key={e.message}>
                {e.message}
              </p>
            ))}
          </div>
        )}
      </form>
    </div>
  );
};

export default WatchlistName;
